import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, History as HistoryIcon, Loader2 } from "lucide-react";
import ResultCard from "../components/ResultCard";

const SEVERITY_DOT = {
  low: "bg-emerald-500",
  medium: "bg-amber-400",
  high: "bg-orange-500",
  critical: "bg-red-500",
};

export default function HistoryPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        if (id) {
          const res = await fetch(`${backend}/api/history/${id}`);
          if (!res.ok) throw new Error("not found");
          const j = await res.json();
          if (active) setDetail(j);
        } else {
          const res = await fetch(`${backend}/api/history`);
          if (!res.ok) throw new Error("failed");
          const j = await res.json();
          if (active) setItems(j);
        }
      } catch {
        if (active) setError(id ? t("history.notFound") : t("uploader.errorGeneric"));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [id, backend, t]);

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-24">
      <Link
        to={id ? "/historial" : "/"}
        data-testid="history-back-link"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition"
      >
        <ArrowLeft className="w-4 h-4" />
        {id ? t("history.title") : t("history.backHome")}
      </Link>

      <header className="mt-6 mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            <HistoryIcon className="w-3.5 h-3.5" />
            {t("history.title")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="history-title">
            {id ? t("result.title") : t("history.title")}
          </h1>
          {!id && (
            <p className="text-sm text-muted-foreground mt-1">
              {t("history.subtitle")}
            </p>
          )}
        </div>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground" data-testid="history-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("history.loading")}
        </div>
      )}

      {!loading && error && (
        <div className="text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3 text-sm" data-testid="history-error">
          {error}
        </div>
      )}

      {!loading && !error && id && detail && (
        <ResultCard data={detail} hideReset />
      )}

      {!loading && !error && !id && items && items.length === 0 && (
        <div className="rounded-2xl border border-border bg-card/60 p-10 text-center text-muted-foreground" data-testid="history-empty">
          {t("history.empty")}
        </div>
      )}

      {!loading && !error && !id && items && items.length > 0 && (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card/60 overflow-hidden" data-testid="history-list">
          {items.map((it, idx) => (
            <motion.li
              key={it.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
            >
              <Link
                to={`/historial/${it.id}`}
                data-testid={`history-item-${it.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-accent/40 transition"
              >
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    SEVERITY_DOT[it.severity] || SEVERITY_DOT.medium
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">
                    {it.faulty_component} · <span className="text-muted-foreground font-normal">{it.panic_type}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {it.device_model} · {it.ios_version}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground hidden sm:block">
                  <div>{new Date(it.created_at).toLocaleDateString(i18n.resolvedLanguage)}</div>
                  <div className="font-mono mt-0.5">{it.confidence}%</div>
                </div>
              </Link>
            </motion.li>
          ))}
        </ul>
      )}
    </main>
  );
}
